import React, { useState, useEffect, useContext } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import LocalView from './LocalView';
import useSystemDetailData from '../hooks/useSystemDetailData';
import {
  UNMET_NEEDS_DISPLAY_NAMES,
  DEFAULT_PRIORITY_UNMET_NEED
} from '../../../constants/unmetNeeds';
import useResolvedSystem from '../hooks/useResolvedSystem';
import { AuthContext } from '../../../context/AuthContext';
import { syncAuthTokens } from '../../../utils/api';
import '../styles.css';

const SystemDetailPage = () => {
  const { systemName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get tokens from main app's AuthContext
  const authContext = useContext(AuthContext);
  const accessToken = authContext?.accessToken;
  const refreshToken = authContext?.refreshToken;
  
  // Sync tokens with the api utility when they're available
  useEffect(() => {
    if (accessToken && refreshToken) {
      syncAuthTokens(accessToken, refreshToken);
    }
  }, [accessToken, refreshToken]);

  const [priorityUnmetNeed, setPriorityUnmetNeed] = useState(DEFAULT_PRIORITY_UNMET_NEED);
  const {
    system: selectedSystem,
    loading: systemLookupLoading,
    error: systemLookupError,
  } = useResolvedSystem(systemName, location.state?.system);
  const {
    systemProfileData,
    keyUnmetNeedsData,
    siteTreemapData,
    hospitalDistributionData,
    physicianDistributionData,
    loading,
    error,
  } = useSystemDetailData(selectedSystem?.id, priorityUnmetNeed);

  // Handle priority unmet need change
  const handlePriorityNeedChange = (event) => {
    setPriorityUnmetNeed(event.target.value);
  };

  // Handle back button click
  const handleBackClick = () => {
    // Navigate back to the root of the asthma tracker
    navigate('/asthma_tracker'); 
  };

  if (systemLookupLoading) {
    return <div className="loading-container">Loading health system identifier...</div>;
  }

  if (!selectedSystem) {
    return (
      <div className="error-container">
        <div>{systemLookupError || 'No health system data found. Please return to selection.'}</div>
        <button className="back-button" onClick={handleBackClick}>
          ← Back to Health Systems
        </button>
      </div>
    );
  }

  if (loading) {
    return <div className="loading-container">Loading details for {selectedSystem.name}...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div>Error loading data for {selectedSystem.name}: {error}</div>
        <button className="back-button" onClick={handleBackClick}>
          ← Back to Health Systems
        </button>
      </div>
    );
  }

  return (
    <div className="asthma-tracker-container">
      <div className="system-detail-header">
        <button className="back-button" onClick={handleBackClick}>
          ← Back to Health Systems
        </button>
        <h1 className="page-title">{selectedSystem.name}</h1>
      </div>

      <div className="view-content">
        <LocalView
          selectedSystem={selectedSystem}
          priorityUnmetNeed={priorityUnmetNeed}
          systemProfileData={systemProfileData}
          keyUnmetNeedsData={keyUnmetNeedsData}
          siteTreemapData={siteTreemapData}
          hospitalDistributionData={hospitalDistributionData}
          physicianDistributionData={physicianDistributionData}
          handlePriorityNeedChange={handlePriorityNeedChange}
          unmetNeedsDisplayNames={UNMET_NEEDS_DISPLAY_NAMES}
        />
      </div>
    </div>
  );
};

export default SystemDetailPage; 
